import React, {FC} from 'react';
import styled from 'styled-components';

import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import {RelatedKnowledgeReference} from '../../../../../models/api/relatedKnowledgeModel';
import {SynopsisMessageDetail} from './synopsisHelpers';
import {useSynopsisViewerContext} from './synopsisViewerContext';

/*
 * Props.
 */

interface SynopsisRelatedKnowledgeReferenceProps {
  detail: SynopsisMessageDetail;
  relatedKnowledgeReference: RelatedKnowledgeReference;
}

/*
 * Style.
 */

const StyledRelatedKnowledgeReferenceButton = styled(ChromelessButton)`
  display: grid;
  align-items: center;
  grid-template-columns: 16px 1fr;
  grid-row-gap: 4px;
  grid-column-gap: 6px;
  padding: 10px 12px;
  max-width: 400px;
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.alphas.black20};

  &:hover {
    background: ${(p) => p.theme.alphas.gray10};
  }
`;

const StyledRelatedKnowledgeReferenceTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
  line-height: 20px;
  ${ellipsis()}
`;

const StyledRelatedKnowledgeReferenceContentDiv = styled.div`
  grid-column-start: 2;
  font-size: ${fontSizes.small};
  ${ellipsis()}
`;

/*
 * Component.
 */

export const SynopsisRelatedKnowledgeReference: FC<SynopsisRelatedKnowledgeReferenceProps> = ({
  relatedKnowledgeReference,
  detail,
}) => {
  const {onTrackDataEvent} = useSynopsisViewerContext();

  const onClick = () => {
    // Articles from internal KBs don't have a URL.
    // @TODO: Find a solution to access the content of internal KB articles.
    if (relatedKnowledgeReference.url) {
      openInBrowser(relatedKnowledgeReference.url);
      onTrackDataEvent(DataEventActionsEnum.CLICK, ViewerDataEventObject.SYNOPSIS_ITEM_RELATED, {
        related_id: relatedKnowledgeReference.id,
        related_type: 'knowledge_sources',
        activity_id: detail.activityId,
        type: detail.type,
      });
    }
  };

  return (
    <StyledRelatedKnowledgeReferenceButton onClick={onClick}>
      <Icon name="knowledgeBaseSmall" />
      <StyledRelatedKnowledgeReferenceTitleDiv>
        <TooltipCoordinator render={() => relatedKnowledgeReference.title}>
          <TooltipOverflowCondition>{relatedKnowledgeReference.title}</TooltipOverflowCondition>
        </TooltipCoordinator>
      </StyledRelatedKnowledgeReferenceTitleDiv>
      {relatedKnowledgeReference.content && (
        <StyledRelatedKnowledgeReferenceContentDiv>
          {relatedKnowledgeReference.content}
        </StyledRelatedKnowledgeReferenceContentDiv>
      )}
    </StyledRelatedKnowledgeReferenceButton>
  );
};
